import { useMemo, useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Search, StickyNote } from "lucide-react";
import { useOrg } from "@/lib/org/OrgContext";
import { useHubConnection } from "@/lib/hub/useHubConnection";
import { useToastMutation } from "@/lib/mutations";
import { notesApi, notesQueries, type NoteSummaryDto } from "@/lib/notes/api";
import { ApiError } from "@/api/fetch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { PageContainer } from "@/components/layout/WorkspaceShell";
import { PageHeader } from "@/components/layout/PageHeader";
import { formatRelative } from "@/lib/format";
import { AlertTriangle } from "lucide-react";

export const Route = createFileRoute("/o/$slug/notes/")({
  component: NotesListPage,
});

function NotesListPage() {
  const org = useOrg();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [filter, setFilter] = useState("");

  const list = useQuery(notesQueries.list(org.slug));

  // Live updates: any NoteUpdated / NoteDeleted on a note in this org should
  // invalidate the list so previews and timestamps stay current.
  useHubConnection({
    path: "/hubs/notes",
    on: {
      NoteUpdated: () => queryClient.invalidateQueries({ queryKey: notesQueries.list(org.slug).queryKey }),
      NoteDeleted: () => queryClient.invalidateQueries({ queryKey: notesQueries.list(org.slug).queryKey }),
    },
  });

  const create = useToastMutation({
    mutationFn: () => notesApi.create(org.slug, { title: "Neue Notiz", body: "" }),
    toasts: { success: "Notiz angelegt." },
    onSuccess: (note) => {
      queryClient.invalidateQueries({ queryKey: notesQueries.list(org.slug).queryKey });
      void navigate({ to: "/o/$slug/notes/$noteId", params: { slug: org.slug, noteId: note.id } });
    },
  });

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const data = list.data ?? [];
    if (!q) return data;
    return data.filter(
      (n) =>
        n.title.toLowerCase().includes(q) || n.preview.toLowerCase().includes(q),
    );
  }, [list.data, filter]);

  return (
    <PageContainer>
      <PageHeader
        title="Notizen"
        description="Org-weite Notizen mit optionaler Unternehmens-Markierung."
        actions={
          <Button
            onClick={() => create.mutate(undefined)}
            disabled={create.isPending}
          >
            <Plus className="h-4 w-4" />
            Neue Notiz
          </Button>
        }
      />

      <div className="mb-4 relative">
        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Suchen…"
          className="pl-8 max-w-sm"
        />
      </div>

      {list.isLoading && <ListSkeleton />}

      {list.error && (
        <ErrorBox error={list.error} onRetry={() => void list.refetch()} />
      )}

      {list.data && filtered.length === 0 && (
        <EmptyState filter={filter} hasNotes={list.data.length > 0} />
      )}

      {filtered.length > 0 && (
        <ul className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
          {[...filtered]
            .sort((a, b) => +new Date(b.updatedAt) - +new Date(a.updatedAt))
            .map((n) => (
              <li key={n.id}>
                <NoteCard slug={org.slug} note={n} />
              </li>
            ))}
        </ul>
      )}
    </PageContainer>
  );
}

function NoteCard({ slug, note }: { slug: string; note: NoteSummaryDto }) {
  return (
    <Link
      to="/o/$slug/notes/$noteId"
      params={{ slug, noteId: note.id }}
      className="group block h-full rounded-lg border bg-card p-4 transition-colors hover:bg-accent/50"
    >
      <div className="flex items-start gap-2">
        <StickyNote className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground group-hover:text-foreground" />
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium">{note.title || "Ohne Titel"}</div>
          {note.preview && (
            <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">{note.preview}</p>
          )}
          <div className="mt-2 text-xs text-muted-foreground">
            {formatRelative(note.updatedAt) ?? "—"}
          </div>
        </div>
      </div>
    </Link>
  );
}

function EmptyState({ filter, hasNotes }: { filter: string; hasNotes: boolean }) {
  if (filter && hasNotes) {
    return (
      <div className="rounded-md border bg-card p-6 text-center">
        <p className="text-sm text-muted-foreground">
          Keine Treffer für „{filter}".
        </p>
      </div>
    );
  }
  return (
    <div className="rounded-md border bg-card p-12 text-center">
      <StickyNote className="mx-auto h-8 w-8 text-muted-foreground" />
      <p className="mt-3 text-sm text-muted-foreground">
        Noch keine Notizen. Lege oben rechts deine erste an.
      </p>
    </div>
  );
}

function ListSkeleton() {
  return (
    <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
      {Array.from({ length: 6 }).map((_, i) => (
        <Skeleton key={i} className="h-24" />
      ))}
    </div>
  );
}

function ErrorBox({ error, onRetry }: { error: Error; onRetry: () => void }) {
  const status = error instanceof ApiError ? error.status : null;
  return (
    <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4">
      <div className="flex items-start gap-2 text-sm text-destructive">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        <span>
          {status === 403
            ? "Keine Berechtigung."
            : `Konnte Notizen nicht laden: ${error.message}`}
        </span>
      </div>
      <Button variant="outline" size="sm" className="mt-3" onClick={onRetry}>
        Erneut versuchen
      </Button>
    </div>
  );
}
